import React, { useRef, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";

export interface SchemaProperty {
  name: string;
  type: string;
  description?: string;
  required?: boolean;
  enum?: string[];
  format?: string;
  properties?: { [key: string]: SchemaProperty };
  items?: SchemaProperty; // For array types
  _id?: string; // Add stable ID for React keys
}

interface SchemaPropertyEditorProps {
  properties: { [key: string]: SchemaProperty };
  onChange: (properties: { [key: string]: SchemaProperty }) => void;
  level?: number;
}

const SchemaPropertyEditor: React.FC<SchemaPropertyEditorProps> = ({ 
  properties, 
  onChange, 
  level = 0 
}) => {
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
  const idMapRef = useRef<Map<string, string>>(new Map());
  const [enumTextMap, setEnumTextMap] = useState<Record<string, string>>({});

  const getStableKey = (propName: string, prop: SchemaProperty): string => {
    // Prefer existing cached key for this property name
    const existing = idMapRef.current.get(propName);
    if (existing) return existing;
    // Otherwise use provided _id or generate one, then cache it
    const newKey = prop._id || `${propName}_${Date.now()}_${Math.random()}`;
    idMapRef.current.set(propName, newKey);
    return newKey;
  };

  const handleAddProperty = () => {
    const newPropertyName = `property_${Object.keys(properties).length + 1}`;
    const newProperty: SchemaProperty = {
      name: newPropertyName,
      type: "string",
      description: "",
      required: false,
      _id: `${Date.now()}_${Math.random()}`
    };

    onChange({
      ...properties,
      [newPropertyName]: newProperty
    });
  };

  const handleRemoveProperty = (propertyName: string) => {
    const newProperties = { ...properties };
    delete newProperties[propertyName];
    const key = idMapRef.current.get(propertyName);
    setEnumTextMap((prev) => {
      if (!key) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    onChange(newProperties);
  };

  const handlePropertyChange = (
    propertyName: string, 
    field: keyof SchemaProperty, 
    value: any
  ) => {
    const newProperties = { ...properties };
    
    if (field === "name" && value !== propertyName) {
      // Handle property rename - check for collision first to prevent data loss
      if (newProperties[value]) {
        // Property name already exists - don't allow the rename
        console.warn(`Property name "${value}" already exists. Rename cancelled to prevent data loss.`);
        return;
      }
      
      // Handle property rename - preserve the _id to maintain component identity
      const property = newProperties[propertyName];
      delete newProperties[propertyName];
      const preservedId = property._id || `${Date.now()}_${Math.random()}`;
      newProperties[value] = { ...property, name: value, _id: preservedId };
      // Move cached key to the new property name to avoid remounts
      const cached = idMapRef.current.get(propertyName);
      if (cached) {
        idMapRef.current.set(value, cached);
        idMapRef.current.delete(propertyName);
      }
    } else {
      const currentProperty = newProperties[propertyName];
      newProperties[propertyName] = {
        ...currentProperty,
        [field]: value,
        // Ensure _id is preserved or created if it doesn't exist
        _id: currentProperty._id || `${Date.now()}_${Math.random()}`
      };
      
      // Auto-expand when type is changed to "object" or "array"
      if (field === "type" && (value === "object" || value === "array")) {
        setExpandedProperties(prev => new Set(Array.from(prev).concat(propertyName)));
      }
    }
    
    onChange(newProperties);
  };

  const handleNestedPropertyChange = (
    parentPropertyName: string, 
    nestedProperties: { [key: string]: SchemaProperty }
  ) => {
    const newProperties = { ...properties };
    newProperties[parentPropertyName] = {
      ...newProperties[parentPropertyName],
      properties: nestedProperties
    };
    onChange(newProperties);
  };

  const handleArrayItemChange = (
    propertyName: string, 
    itemProperty: SchemaProperty
  ) => {
    const newProperties = { ...properties };
    newProperties[propertyName] = {
      ...newProperties[propertyName],
      items: itemProperty
    };
    onChange(newProperties);
  };

  const toggleExpanded = (propertyName: string) => {
    const newExpanded = new Set(expandedProperties);
    if (newExpanded.has(propertyName)) {
      newExpanded.delete(propertyName);
    } else {
      newExpanded.add(propertyName);
    }
    setExpandedProperties(newExpanded);
  };

  const handleEnumChange = (propertyName: string, enumValues: string) => {
    const enumArray = enumValues
      .split(",")
      .map(val => val.trim())
      .filter(val => val.length > 0);
    
    handlePropertyChange(propertyName, "enum", enumArray.length > 0 ? enumArray : undefined);
  };

  const isExpanded = (propertyName: string) => expandedProperties.has(propertyName);

  if (Object.keys(properties).length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-border rounded-lg">
        <p className="text-sm text-muted-foreground mb-4">No properties defined.</p>
        <Button onClick={handleAddProperty} variant="outline" size="sm" className="gap-2">
          <Plus className="w-4 h-4" />
          Add Property
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(properties).map(([propertyName, property]) => {
        // Ensure property has a stable key that does not change during edits
        const stableKey = getStableKey(propertyName, property);
        
        return (
        <div
          key={stableKey}
          className={`border border-border rounded-lg ${level > 0 ? "ml-4" : ""}`}
        >
          <div className="p-4 space-y-4">
            {/* Property Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <Input
                  value={property.name}
                  onChange={(e) => handlePropertyChange(propertyName, "name", e.target.value)}
                  placeholder="Property name"
                  className="font-mono text-sm flex-1"
                />
                <Select
                  value={property.type}
                  onValueChange={(value) => handlePropertyChange(propertyName, "type", value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="integer">Integer</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="array">Array</SelectItem>
                    <SelectItem value="object">Object</SelectItem>
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={property.required || false}
                    onChange={(e) => handlePropertyChange(propertyName, "required", e.target.checked)}
                    className="rounded"
                  />
                  Required
                </label>
              </div>
              <div className="flex items-center gap-2">
                {(property.type === "object" || property.type === "array") && (
                  <Button
                    onClick={() => toggleExpanded(propertyName)}
                    variant="ghost"
                    size="sm"
                    className="p-1"
                  >
                    {isExpanded(propertyName) ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </Button>
                )}
                <Button
                  onClick={() => handleRemoveProperty(propertyName)}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Property Details */}
            <div className="space-y-3">
              <Input
                value={property.description || ""}
                onChange={(e) => handlePropertyChange(propertyName, "description", e.target.value)}
                placeholder="Property description (optional)"
                className="text-sm"
              />

              {/* Format field for strings */}
              {property.type === "string" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Format (optional)</Label>
                    <Input
                      value={property.format || ""}
                      onChange={(e) => handlePropertyChange(propertyName, "format", e.target.value)}
                      placeholder="email, date, uri, etc."
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Enum values (comma-separated)</Label>
                    <Input
                      value={enumTextMap[stableKey] ?? property.enum?.join(", ") ?? ""}
                      onChange={(e) =>
                        setEnumTextMap((prev) => ({ ...prev, [stableKey]: e.target.value }))
                      }
                      onBlur={(e) => {
                        const text = e.target.value;
                        const enumArray = text
                          .split(",")
                          .map((val) => val.trim())
                          .filter((val) => val.length > 0);
                        handlePropertyChange(
                          propertyName,
                          "enum",
                          enumArray.length > 0 ? enumArray : undefined
                        );
                        setEnumTextMap((prev) => {
                          const next = { ...prev };
                          delete next[stableKey];
                          return next;
                        });
                      }}
                      placeholder="value1, value2, value3"
                      className="text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Enum field for non-strings */}
              {property.type !== "string" && property.type !== "object" && property.type !== "array" && (
                <div>
                  <Label className="text-xs text-muted-foreground">Enum values (comma-separated)</Label>
                  <Input
                    value={enumTextMap[stableKey] ?? property.enum?.join(", ") ?? ""}
                    onChange={(e) =>
                      setEnumTextMap((prev) => ({ ...prev, [stableKey]: e.target.value }))
                    }
                    onBlur={(e) => {
                      const text = e.target.value;
                      const enumArray = text
                        .split(",")
                        .map((val) => val.trim())
                        .filter((val) => val.length > 0);
                      handlePropertyChange(
                        propertyName,
                        "enum",
                        enumArray.length > 0 ? enumArray : undefined
                      );
                      setEnumTextMap((prev) => {
                        const next = { ...prev };
                        delete next[stableKey];
                        return next;
                      });
                    }}
                    placeholder="value1, value2, value3"
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {/* Nested Object Properties */}
            {property.type === "object" && isExpanded(propertyName) && (
              <div className="border-t border-border pt-4">
                <Label className="text-sm font-medium mb-4 block">Object Properties</Label>
                <SchemaPropertyEditor
                  properties={property.properties || {}}
                  onChange={(nestedProps) => handleNestedPropertyChange(propertyName, nestedProps)}
                  level={level + 1}
                />
              </div>
            )}

            {/* Array Item Configuration */}
            {property.type === "array" && isExpanded(propertyName) && (
              <div className="border-t border-border pt-4">
                <Label className="text-sm font-medium mb-4 block">Array Item Type</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Select
                      value={property.items?.type || "string"}
                      onValueChange={(value) => {
                        const newItem: SchemaProperty = {
                          name: "item",
                          type: value,
                          description: property.items?.description || "",
                          required: property.items?.required || false
                        };
                        handleArrayItemChange(propertyName, newItem);
                      }}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="string">String</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="integer">Integer</SelectItem>
                        <SelectItem value="boolean">Boolean</SelectItem>
                        <SelectItem value="object">Object</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={property.items?.description || ""}
                      onChange={(e) => {
                        const newItem: SchemaProperty = {
                          ...(property.items || {}),
                          name: "item",
                          type: property.items?.type || "string",
                          description: e.target.value
                        };
                        handleArrayItemChange(propertyName, newItem);
                      }}
                      placeholder="Item description"
                      className="text-sm flex-1"
                    />
                  </div>

                  {/* Nested object properties for array items */}
                  {property.items?.type === "object" && (
                    <div className="mt-4">
                      <Label className="text-sm font-medium mb-4 block">Item Object Properties</Label>
                      <SchemaPropertyEditor
                        properties={property.items.properties || {}}
                        onChange={(itemProps) => {
                          const newItem: SchemaProperty = {
                            ...(property.items || {}),
                            name: "item",
                            type: "object",
                            properties: itemProps
                          };
                          handleArrayItemChange(propertyName, newItem);
                        }}
                        level={level + 1}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })}

      {/* Add Property Button */}
      <div className="flex justify-center pt-4">
        <Button onClick={handleAddProperty} variant="outline" size="sm" className="gap-2">
          <Plus className="w-4 h-4" />
          Add Property
        </Button>
      </div>
    </div>
  );
};

export default SchemaPropertyEditor; 